<template>
  <div class="tenant-invoice-records">
    <div class="invoice-records-toolbar">
      <el-button size="small" icon="el-icon-refresh" :loading="loading" @click="load">
        {{ $t('tenantDetailPage.invoiceRecords.refresh') }}
      </el-button>
    </div>
    <el-alert v-if="error" :title="error" type="error" :closable="false" show-icon />
    <div class="invoice-records-table">
      <el-table v-loading="loading" :data="records" :empty-text="emptyText" row-key="id" height="100%" size="small" stripe>
        <el-table-column :label="$t('tenantDetailPage.invoiceRecords.appliedAt')" width="160">
          <template slot-scope="{ row }">{{ displayTime(row.createTime) }}</template>
        </el-table-column>
        <el-table-column :label="$t('tenantDetailPage.invoiceRecords.subject')" width="110">
          <template slot-scope="{ row }">{{ subjectLabel(row.suject) }}</template>
        </el-table-column>
        <el-table-column :label="$t('tenantDetailPage.invoiceRecords.method')" width="120">
          <template slot-scope="{ row }">{{ billTypeLabel(row.billType) }}</template>
        </el-table-column>
        <el-table-column :label="$t('tenantDetailPage.invoiceRecords.title')" prop="enterpriseName" min-width="140" show-overflow-tooltip />
        <el-table-column :label="$t('tenantDetailPage.invoiceRecords.amount')" width="120" align="right">
          <template slot-scope="{ row }">{{ money(row.invoiceAmt) }}</template>
        </el-table-column>
        <el-table-column :label="$t('tenantDetailPage.invoiceRecords.status')" width="100" align="center">
          <template slot-scope="{ row }"><el-tag size="small" effect="plain" :type="statusTag(row.status)">{{ statusLabel(row.status) }}</el-tag></template>
        </el-table-column>
        <el-table-column :label="$t('tenantDetailPage.invoiceRecords.invoiceNo')" prop="fphm" min-width="160" show-overflow-tooltip />
        <el-table-column :label="$t('tenantDetailPage.invoiceRecords.actions')" width="120" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" @click="openDetail(row)">{{ $t('tenantDetailPage.invoiceRecords.detail') }}</el-button>
            <InvoiceRedFlushButton :record="row" @refresh="load" />
          </template>
        </el-table-column>
      </el-table>
    </div>
    <el-pagination
      class="invoice-records-pagination"
      :current-page="page.current"
      :page-size="page.size"
      :total="total"
      :disabled="loading"
      layout="total, prev, pager, next"
      @current-change="changePage"
    />
    <TenantInvoiceDetailDrawer :visible.sync="detailVisible" :record="detailRecord" :manager-id="managerId" read-only />
  </div>
</template>

<script>
import api from '@/api/tenantInvoice'
import { getApiErrorMessage } from '@/utils/apiError'
import TenantInvoiceDetailDrawer from '@/views/console/invoice/components/TenantInvoiceDetailDrawer.vue'
import InvoiceRedFlushButton from '@/views/console/invoice/components/InvoiceRedFlushButton.vue'
import { buildInvoiceQuery, normalizeInvoice, money, displayTime, subjectLabel, billTypeLabel, statusLabel, statusTag } from '@/views/console/invoice/tenantInvoiceModel'

export default {
  name: 'TenantInvoiceRecords',
  components: { TenantInvoiceDetailDrawer, InvoiceRedFlushButton },
  props: {
    tenantId: { type: [String, Number], required: true },
    managerId: { type: [String, Number], required: true }
  },
  data () {
    return {
      page: { current: 1, size: 15 },
      total: 0,
      records: [],
      loading: false,
      error: '',
      version: 0,
      detailVisible: false,
      detailRecord: {}
    }
  },
  computed: {
    emptyText () {
      return this.$t(this.error ? 'tenantDetailPage.invoiceRecords.loadFailed' : 'tenantDetailPage.invoiceRecords.empty')
    }
  },
  created () { this.load() },
  beforeDestroy () { this.version++ },
  methods: {
    money,
    displayTime,
    subjectLabel,
    billTypeLabel,
    statusLabel,
    statusTag,
    async load () {
      const version = ++this.version
      this.loading = true
      this.error = ''
      try {
        const query = buildInvoiceQuery(this.managerId, { tenantId: this.tenantId }, this.page)
        const { data } = await api.page(query)
        if (version !== this.version) return
        if (!data || !Array.isArray(data.dataList) || data.total == null || !Number.isFinite(Number(data.total))) {
          throw new Error(this.$t('tenantDetailPage.invoiceRecords.loadFailed'))
        }
        const inScope = data.dataList.every(row => row.id != null && String(row.tenantId) === String(this.tenantId) &&
          (row.managerId == null || String(row.managerId) === String(this.managerId)))
        if (!inScope) throw new Error(this.$t('tenantDetailPage.invoiceRecords.scopeMismatch'))

        this.records = data.dataList.map(row => normalizeInvoice({ ...row, managerId: this.managerId }))
        this.total = Number(data.total)
      } catch (error) {
        if (version !== this.version) return
        this.records = []
        this.error = getApiErrorMessage(error, this.$t('tenantDetailPage.invoiceRecords.loadFailed'))
      } finally {
        if (version === this.version) this.loading = false
      }
    },
    changePage (current) {
      this.page.current = current
      this.load()
    },
    openDetail (row) {
      this.detailRecord = row
      this.detailVisible = true
    }
  }
}
</script>

<style scoped>
.tenant-invoice-records {
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 12px;
}
.invoice-records-toolbar { display: flex; justify-content: flex-end; }
.invoice-records-table { flex: 1; min-height: 0; }
.invoice-records-pagination { align-self: flex-end; }
</style>
